import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import QRCode from 'qrcode';
import './QRManagement.css';

const QRManagement = () => {
  const [activeTab, setActiveTab] = useState('qrcodes'); // 'qrcodes' | 'categories' | 'reclamations' | 'stats'

  // Data states
  const [locations, setLocations] = useState([]);
  const [equipments, setEquipments] = useState([]);
  const [reclamations, setReclamations] = useState([]);
  const [stats, setStats] = useState(null);

  // Motifs de pannes / Catégories par localisation
  const [selectedLocForCat, setSelectedLocForCat] = useState('');
  const [locationCategories, setLocationCategories] = useState([]);
  const [newCatName, setNewCatName] = useState('');
  const [newCatType, setNewCatType] = useState('reclamation');
  const [loadingCats, setLoadingCats] = useState(false);

  // Filter states
  const [selectedLocationFilter, setSelectedLocationFilter] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');

  // Form states - Nouvel Équipement
  const [newLocationName, setNewLocationName] = useState('');
  const [selectedLocationForEquip, setSelectedLocationForEquip] = useState('');
  const [newEquipName, setNewEquipName] = useState('');
  const [newEquipCategory, setNewEquipCategory] = useState('Chaise de massage');

  // Photo Lightbox modal
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  // QR Poster Modal
  const [posterEquip, setPosterEquip] = useState(null);
  const [posterQrUrl, setPosterQrUrl] = useState('');

  // Loading states
  const [submittingLoc, setSubmittingLoc] = useState(false);
  const [submittingEquip, setSubmittingEquip] = useState(false);
  const [submittingCat, setSubmittingCat] = useState(false);

  useEffect(() => {
    fetchLocations();
    fetchEquipments();
    fetchReclamations();
    fetchStats();
  }, []);

  useEffect(() => {
    if (selectedLocForCat) {
      fetchLocationCategories(selectedLocForCat);
    }
  }, [selectedLocForCat]);

  const fetchLocations = async () => {
    try {
      const res = await axios.get('/api/Locations/getAllLocations');
      const locList = Array.isArray(res.data) ? res.data : (res.data.locations || []);
      setLocations(locList);
      if (locList.length > 0 && !selectedLocForCat) {
        setSelectedLocForCat(locList[0]._id);
      }
    } catch (err) {
      console.error('Erreur chargement localisations:', err);
    }
  };

  const fetchEquipments = async () => {
    try {
      const res = await axios.get('/api/qr-equipments');
      if (res.data.success) {
        setEquipments(res.data.equipments);
      }
    } catch (err) {
      console.error('Erreur chargement équipements:', err);
    }
  };

  const fetchReclamations = async () => {
    try {
      const res = await axios.get('/api/reclamations');
      if (res.data.success) {
        setReclamations(res.data.reclamations);
      }
    } catch (err) {
      console.error('Erreur chargement réclamations:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/reclamations/stats');
      if (res.data.success) {
        setStats(res.data.stats);
      }
    } catch (err) {
      console.error('Erreur chargement stats:', err);
    }
  };

  const fetchLocationCategories = async (locationId) => {
    if (!locationId) return;
    try {
      setLoadingCats(true);
      const res = await axios.get(`/api/qr-categories/location/${locationId}`);
      if (res.data.success) {
        setLocationCategories(res.data.categories);
      }
    } catch (err) {
      console.error('Erreur chargement motifs:', err);
    } finally {
      setLoadingCats(false);
    }
  };

  // Créer une nouvelle localisation
  const handleCreateLocation = async (e) => {
    e.preventDefault();
    if (!newLocationName.trim()) return;
    try {
      setSubmittingLoc(true);
      await axios.post('/api/Locations/addLocation', { name: newLocationName.trim() });
      setNewLocationName('');
      fetchLocations();
      alert('Localisation créée avec succès !');
    } catch (err) {
      console.error('Erreur création localisation:', err);
      alert(err.response?.data?.message || 'Erreur lors de la création de la localisation.');
    } finally {
      setSubmittingLoc(false);
    }
  };

  // Créer un nouvel équipement / spot QR
  const handleCreateEquipment = async (e) => {
    e.preventDefault();
    if (!selectedLocationForEquip || !newEquipName.trim()) {
      alert('Veuillez sélectionner une localisation et donner un nom à l\'équipement.');
      return;
    }

    try {
      setSubmittingEquip(true);
      const res = await axios.post('/api/qr-equipments', {
        location: selectedLocationForEquip,
        name: newEquipName.trim(),
        category: newEquipCategory
      });

      if (res.data.success) {
        setNewEquipName('');
        fetchEquipments();
        alert(`Équipement "${res.data.equipment.name}" ajouté avec son QR code !`);
      }
    } catch (err) {
      console.error('Erreur création équipement:', err);
      alert(err.response?.data?.message || 'Erreur lors de l\'ajout de l\'équipement.');
    } finally {
      setSubmittingEquip(false);
    }
  };

  // Ajouter un motif / type de panne personnalisé pour une localisation
  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!selectedLocForCat || !newCatName.trim()) {
      alert('Veuillez sélectionner une localisation et saisir le nom du motif.');
      return;
    }

    try {
      setSubmittingCat(true);
      const res = await axios.post('/api/qr-categories', {
        location: selectedLocForCat,
        name: newCatName.trim(),
        type: newCatType
      });

      if (res.data.success) {
        setNewCatName('');
        fetchLocationCategories(selectedLocForCat);
        alert('Motif de panne ajouté !');
      }
    } catch (err) {
      console.error('Erreur création motif:', err);
      alert('Erreur lors de l\'ajout du motif.');
    } finally {
      setSubmittingCat(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Voulez-vous supprimer ce motif de panne de la liste ?')) return;
    try {
      const res = await axios.delete(`/api/qr-categories/${id}`);
      if (res.data.success) {
        fetchLocationCategories(selectedLocForCat);
      }
    } catch (err) {
      alert('Erreur lors de la suppression.');
    }
  };

  const handleDeleteEquipment = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cet équipement QR ?')) return;
    try {
      const res = await axios.delete(`/api/qr-equipments/${id}`);
      if (res.data.success) {
        fetchEquipments();
      }
    } catch (err) {
      alert('Erreur lors de la suppression de l\'équipement.');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await axios.put(`/api/reclamations/${id}/status`, { status: newStatus });
      if (res.data.success) {
        fetchReclamations();
        fetchStats();
      }
    } catch (err) {
      alert('Erreur lors du changement de statut.');
    }
  };

  const handleDeleteReclamation = async (id) => {
    if (!window.confirm('Voulez-vous supprimer cette réclamation ?')) return;
    try {
      const res = await axios.delete(`/api/reclamations/${id}`);
      if (res.data.success) {
        fetchReclamations();
        fetchStats();
      }
    } catch (err) {
      alert('Erreur lors de la suppression.');
    }
  };

  const getClientFormUrl = (qrToken) => {
    const origin = window.location.origin;
    return `${origin}/reclamation/${qrToken}`;
  };

  const handleOpenPoster = async (equip) => {
    const url = getClientFormUrl(equip.qrToken);
    try {
      const dataUrl = await QRCode.toDataURL(url, { width: 300, margin: 2 });
      setPosterQrUrl(dataUrl);
      setPosterEquip(equip);
    } catch (err) {
      console.error('Erreur génération QR:', err);
    }
  };

  const handlePrintPoster = () => {
    window.print();
  };

  const handleCopyUrl = (qrToken) => {
    const url = getClientFormUrl(qrToken);
    navigator.clipboard.writeText(url);
    alert(`Lien copié dans le presse-papier :\n${url}`);
  };

  const filteredReclamations = reclamations.filter(rec => {
    if (selectedLocationFilter && rec.location?._id !== selectedLocationFilter) return false;
    if (selectedStatusFilter && rec.status !== selectedStatusFilter) return false;
    if (selectedTypeFilter && rec.type !== selectedTypeFilter) return false;
    return true;
  });

  const pendingCount = reclamations.filter(r => r.status === 'en_attente').length;

  const QRCanvas = ({ url }) => {
    const canvasRef = useRef(null);
    useEffect(() => {
      if (canvasRef.current && url) {
        QRCode.toCanvas(canvasRef.current, url, { width: 85, margin: 1 }, (err) => {
          if (err) console.error(err);
        });
      }
    }, [url]);
    return <canvas ref={canvasRef} style={{ borderRadius: '8px', border: '1px solid #cbd5e1', background: '#ffffff', padding: '4px' }} />;
  };

  return (
    <div className="qr-admin-container">
      {/* En-tête de page principal */}
      <div className="qr-admin-header-card">
        <div className="qr-title-box">
          <h2>📱 Gestion QR Codes & Réclamations Clients</h2>
          <p className="qr-subtitle">Générez vos QR codes, personnalisez les motifs de pannes par site et analysez les signalements</p>
        </div>

        {/* Barre d'onglets ergonomique */}
        <div className="qr-tab-nav">
          <button
            className={`qr-tab-btn ${activeTab === 'qrcodes' ? 'active' : ''}`}
            onClick={() => setActiveTab('qrcodes')}
          >
            📍 Localisations & QR Codes
          </button>
          <button
            className={`qr-tab-btn ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            ⚙️ Motifs de Pannes
          </button>
          <button
            className={`qr-tab-btn ${activeTab === 'reclamations' ? 'active' : ''}`}
            onClick={() => setActiveTab('reclamations')}
          >
            🔔 Réclamations
            {pendingCount > 0 && <span className="qr-tab-badge">{pendingCount}</span>}
          </button>
          <button
            className={`qr-tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => {
              fetchStats();
              setActiveTab('stats');
            }}
          >
            📊 Analytique des Pannes
          </button>
        </div>
      </div>

      {/* ================= ONGLET 1: LOCALISATIONS & QR CODES ================= */}
      {activeTab === 'qrcodes' && (
        <div className="row g-4">
          <div className="col-lg-4">
            {/* Formulaire Localisation */}
            <div className="qr-card">
              <div className="qr-card-header primary-header">
                <span>📍 Ajouter une Localisation</span>
              </div>
              <div className="qr-card-body">
                <form onSubmit={handleCreateLocation}>
                  <div className="mb-3">
                    <label className="form-label font-weight-bold text-dark small">Nom du site / Localisation *</label>
                    <input
                      type="text"
                      className="form-control form-control-lg fs-6"
                      placeholder="ex: Spa Hammamet, Salon VIP..."
                      value={newLocationName}
                      onChange={(e) => setNewLocationName(e.target.value)}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-dark w-100 fw-bold py-2" disabled={submittingLoc}>
                    + Ajouter la Localisation
                  </button>
                </form>
              </div>
            </div>

            {/* Formulaire Équipement QR */}
            <div className="qr-card">
              <div className="qr-card-header accent-header">
                <span>🛋️ Nouvel Équipement QR Code</span>
              </div>
              <div className="qr-card-body">
                <form onSubmit={handleCreateEquipment}>
                  <div className="mb-3">
                    <label className="form-label font-weight-bold text-dark small">1. Sélectionner la Localisation *</label>
                    <select
                      className="form-select form-select-lg fs-6"
                      value={selectedLocationForEquip}
                      onChange={(e) => setSelectedLocationForEquip(e.target.value)}
                      required
                    >
                      <option value="">-- Choisir un site --</option>
                      {locations.map(loc => (
                        <option key={loc._id} value={loc._id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label font-weight-bold text-dark small">2. Nom de l'Équipement / Chaise *</label>
                    <input
                      type="text"
                      className="form-control form-control-lg fs-6"
                      placeholder="ex: Chaise de Massage #1, Table 4..."
                      value={newEquipName}
                      onChange={(e) => setNewEquipName(e.target.value)}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label font-weight-bold text-dark small">3. Catégorie d'équipement</label>
                    <select
                      className="form-select form-select-lg fs-6"
                      value={newEquipCategory}
                      onChange={(e) => setNewEquipCategory(e.target.value)}
                    >
                      <option value="Chaise de massage">Chaise de massage</option>
                      <option value="Climatiseur / Chauffage">Climatiseur / Chauffage</option>
                      <option value="Mobilier & Assise">Mobilier & Assise</option>
                      <option value="Hygiène & Espace d'accueil">Hygiène & Espace d'accueil</option>
                      <option value="Appareil Électronique">Appareil Électronique</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>

                  <button type="submit" className="btn btn-primary w-100 fw-bold py-2 shadow-sm" disabled={submittingEquip}>
                    ✨ Générer & Enregistrer le QR Code
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="qr-card">
              <div className="qr-card-header">
                <span>📋 Équipements & QR Codes Générés ({equipments.length})</span>
                <button className="btn btn-sm btn-outline-primary fw-bold" onClick={fetchEquipments}>
                  🔄 Actualiser
                </button>
              </div>
              <div className="qr-card-body p-0">
                {equipments.length === 0 ? (
                  <div className="text-center p-5 text-muted">
                    <p className="fs-1 mb-2">📷</p>
                    <h5 className="fw-bold">Aucun QR code créé</h5>
                    <p className="small">Sélectionnez une localisation et ajoutez un équipement à gauche pour générer un QR Code.</p>
                  </div>
                ) : (
                  <div className="qr-table-container">
                    <table className="qr-table">
                      <thead>
                        <tr>
                          <th>Aperçu QR</th>
                          <th>Équipement / Chaise</th>
                          <th>Localisation</th>
                          <th>Catégorie</th>
                          <th className="text-end">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {equipments.map(equip => {
                          const clientUrl = getClientFormUrl(equip.qrToken);
                          return (
                            <tr key={equip._id}>
                              <td style={{ width: '100px' }}>
                                <QRCanvas url={clientUrl} />
                              </td>
                              <td>
                                <strong className="d-block text-dark fs-6">{equip.name}</strong>
                                <span className="badge bg-secondary font-monospace">{equip.qrToken}</span>
                              </td>
                              <td>
                                <span className="badge bg-primary fs-6 px-3 py-2">
                                  📍 {equip.location?.name || 'Localisation inconnue'}
                                </span>
                              </td>
                              <td>
                                <span className="text-muted fw-semibold">{equip.category}</span>
                              </td>
                              <td className="text-end">
                                <button
                                  className="btn btn-sm btn-primary me-2 fw-bold"
                                  onClick={() => handleOpenPoster(equip)}
                                >
                                  🖨️ Affiche QR
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-secondary me-2 fw-bold"
                                  onClick={() => handleCopyUrl(equip.qrToken)}
                                >
                                  🔗 Copier Lien
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDeleteEquipment(equip._id)}
                                >
                                  🗑️
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= ONGLET 2: MOTIFS DE PANNES PAR LOCALISATION ================= */}
      {activeTab === 'categories' && (
        <div className="row g-4">
          <div className="col-lg-4">
            <div className="qr-card">
              <div className="qr-card-header accent-header">
                <span>➕ Ajouter un Motif de Panne</span>
              </div>
              <div className="qr-card-body">
                <form onSubmit={handleCreateCategory}>
                  <div className="mb-3">
                    <label className="form-label font-weight-bold text-dark small">1. Sélectionner la Localisation *</label>
                    <select
                      className="form-select form-select-lg fs-6"
                      value={selectedLocForCat}
                      onChange={(e) => setSelectedLocForCat(e.target.value)}
                      required
                    >
                      <option value="">-- Sélectionner un site --</option>
                      {locations.map(l => (
                        <option key={l._id} value={l._id}>{l.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label font-weight-bold text-dark small">2. Type de Motif</label>
                    <select
                      className="form-select form-select-lg fs-6"
                      value={newCatType}
                      onChange={(e) => setNewCatType(e.target.value)}
                    >
                      <option value="reclamation">⚠️ Motif de Panne / Réclamation</option>
                      <option value="suggestion">💡 Motif de Suggestion</option>
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label font-weight-bold text-dark small">3. Libellé du Motif *</label>
                    <input
                      type="text"
                      className="form-control form-control-lg fs-6"
                      placeholder="ex: Télécommande cassée, Bruit d'air..."
                      value={newCatName}
                      onChange={(e) => setNewCatName(e.target.value)}
                      required
                    />
                  </div>

                  <button type="submit" className="btn btn-primary w-100 fw-bold py-2 shadow-sm" disabled={submittingCat}>
                    + Enregistrer le Motif
                  </button>
                </form>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="qr-card">
              <div className="qr-card-header">
                <span>⚙️ Liste des Motifs de Pannes pour la Localisation Sélectionnée</span>
                <select
                  className="form-select form-select-sm w-auto fw-bold"
                  value={selectedLocForCat}
                  onChange={(e) => setSelectedLocForCat(e.target.value)}
                >
                  {locations.map(l => (
                    <option key={l._id} value={l._id}>📍 {l.name}</option>
                  ))}
                </select>
              </div>
              <div className="qr-card-body p-0">
                {loadingCats ? (
                  <div className="text-center p-4 text-muted">Chargement des motifs...</div>
                ) : locationCategories.length === 0 ? (
                  <div className="text-center p-5 text-muted">
                    <p className="fs-1 mb-2">⚙️</p>
                    <p>Aucun motif configuré pour cette localisation.</p>
                  </div>
                ) : (
                  <div className="qr-table-container">
                    <table className="qr-table">
                      <thead>
                        <tr>
                          <th>Motif / Libellé</th>
                          <th>Type</th>
                          <th className="text-end">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {locationCategories.map(cat => (
                          <tr key={cat._id}>
                            <td>
                              <strong className="text-dark fs-6">{cat.name}</strong>
                            </td>
                            <td>
                              {cat.type === 'reclamation' ? (
                                <span className="badge bg-danger px-3 py-2">⚠️ Panne</span>
                              ) : (
                                <span className="badge bg-primary px-3 py-2">💡 Suggestion</span>
                              )}
                            </td>
                            <td className="text-end">
                              <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleDeleteCategory(cat._id)}
                                title="Supprimer ce motif"
                              >
                                🗑️ Supprimer
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= ONGLET 3: SUIVI DES RÉCLAMATIONS ================= */}
      {activeTab === 'reclamations' && (
        <div className="qr-card">
          <div className="qr-filter-bar">
            <div className="row g-3 align-items-center">
              <div className="col-md-3">
                <label className="form-label small fw-bold text-muted mb-1">Filtrer par Localisation</label>
                <select
                  className="form-select fw-semibold"
                  value={selectedLocationFilter}
                  onChange={(e) => setSelectedLocationFilter(e.target.value)}
                >
                  <option value="">-- Toutes les localisations --</option>
                  {locations.map(l => (
                    <option key={l._id} value={l._id}>{l.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label small fw-bold text-muted mb-1">Filtrer par Statut</label>
                <select
                  className="form-select fw-semibold"
                  value={selectedStatusFilter}
                  onChange={(e) => setSelectedStatusFilter(e.target.value)}
                >
                  <option value="">-- Tous les statuts --</option>
                  <option value="en_attente">⏳ En attente</option>
                  <option value="en_cours">⚙️ En cours d'intervention</option>
                  <option value="resolu">✅ Résolu</option>
                </select>
              </div>

              <div className="col-md-3">
                <label className="form-label small fw-bold text-muted mb-1">Type de Signalement</label>
                <select
                  className="form-select fw-semibold"
                  value={selectedTypeFilter}
                  onChange={(e) => setSelectedTypeFilter(e.target.value)}
                >
                  <option value="">-- Tous les types --</option>
                  <option value="reclamation">⚠️ Réclamations / Pannes</option>
                  <option value="suggestion">💡 Suggestions</option>
                </select>
              </div>

              <div className="col-md-3 text-end pt-3">
                <button className="btn btn-primary fw-bold" onClick={fetchReclamations}>
                  🔄 Actualiser ({filteredReclamations.length})
                </button>
              </div>
            </div>
          </div>

          <div className="qr-card-body p-0">
            {filteredReclamations.length === 0 ? (
              <div className="text-center p-5 text-muted">
                <p className="fs-1 mb-2">🎉</p>
                <h5 className="fw-bold">Aucun signalement trouvé</h5>
                <p className="small">Aucune réclamation ne correspond à vos filtres actuels.</p>
              </div>
            ) : (
              <div className="qr-table-container">
                <table className="qr-table">
                  <thead>
                    <tr>
                      <th>Date / Heure</th>
                      <th>Type & Motif</th>
                      <th>Localisation & Équipement</th>
                      <th>Photo Client</th>
                      <th>Commentaire / Message</th>
                      <th>Statut</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReclamations.map(rec => (
                      <tr key={rec._id} className={rec.status === 'en_attente' ? 'row-pending' : ''}>
                        <td>
                          <strong className="d-block text-dark">
                            {new Date(rec.createdAt).toLocaleDateString('fr-FR')}
                          </strong>
                          <span className="text-muted small">
                            {new Date(rec.createdAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </td>

                        <td>
                          {rec.type === 'reclamation' ? (
                            <span className="badge bg-danger mb-1 px-2 py-1">⚠️ Panne</span>
                          ) : (
                            <span className="badge bg-primary mb-1 px-2 py-1">💡 Suggestion</span>
                          )}
                          <div className="fw-bold text-dark fs-6">{rec.category}</div>
                        </td>

                        <td>
                          <div className="fw-bold text-primary">📍 {rec.location?.name || 'Inconnue'}</div>
                          {rec.equipment && (
                            <div className="text-dark small fw-semibold">🛋️ {rec.equipment.name}</div>
                          )}
                        </td>

                        <td>
                          {rec.photoUrl ? (
                            <div className="qr-photo-thumb-wrapper">
                              <img
                                src={rec.photoUrl}
                                alt="Photo signalement"
                                className="qr-photo-thumb"
                                onClick={() => setSelectedPhoto(rec.photoUrl)}
                                title="Cliquer pour agrandir la photo"
                              />
                              <span className="qr-photo-zoom-icon">🔍</span>
                            </div>
                          ) : (
                            <span className="text-muted small">Aucune photo</span>
                          )}
                        </td>

                        <td style={{ maxWidth: '280px' }}>
                          <p className="small mb-1 text-wrap text-dark font-weight-medium">
                            {rec.description || <em>Aucun commentaire saisi</em>}
                          </p>
                          {(rec.clientName || rec.clientPhone) && (
                            <span className="badge bg-light text-dark border font-monospace">
                              👤 {rec.clientName} {rec.clientPhone ? `(${rec.clientPhone})` : ''}
                            </span>
                          )}
                        </td>

                        <td>
                          {rec.status === 'en_attente' && <span className="badge bg-warning text-dark fs-6 px-3 py-2">⏳ En attente</span>}
                          {rec.status === 'en_cours' && <span className="badge bg-info text-dark fs-6 px-3 py-2">⚙️ En cours</span>}
                          {rec.status === 'resolu' && <span className="badge bg-success fs-6 px-3 py-2">✅ Résolu</span>}
                          {rec.status === 'rejete' && <span className="badge bg-secondary fs-6 px-3 py-2">❌ Rejeté</span>}
                        </td>

                        <td className="text-end">
                          <div className="btn-group btn-group-sm">
                            {rec.status !== 'resolu' && (
                              <button
                                className="btn btn-success fw-bold me-1"
                                onClick={() => handleStatusChange(rec._id, 'resolu')}
                                title="Marquer cette panne comme Résolue"
                              >
                                ✅ Résolu
                              </button>
                            )}
                            {rec.status === 'en_attente' && (
                              <button
                                className="btn btn-info fw-bold text-dark me-1"
                                onClick={() => handleStatusChange(rec._id, 'en_cours')}
                              >
                                ⚙️ En cours
                              </button>
                            )}
                            <button
                              className="btn btn-outline-danger"
                              onClick={() => handleDeleteReclamation(rec._id)}
                              title="Supprimer la réclamation"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ================= ONGLET 4: STATISTIQUES DES PANNES ================= */}
      {activeTab === 'stats' && (
        <div>
          {/* Cartes KPI */}
          <div className="row g-3 mb-4">
            <div className="col-md-3">
              <div className="qr-kpi-card blue">
                <div className="qr-kpi-label">Total Signalements</div>
                <div className="qr-kpi-value">{stats?.kpis?.total || 0}</div>
                <div className="qr-kpi-sub">
                  <strong>{stats?.kpis?.reclamations || 0}</strong> pannes / <strong>{stats?.kpis?.suggestions || 0}</strong> suggestions
                </div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="qr-kpi-card yellow">
                <div className="qr-kpi-label">Pannes En Attente</div>
                <div className="qr-kpi-value text-warning">{stats?.kpis?.pending || 0}</div>
                <div className="qr-kpi-sub">Interventions non démarrées</div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="qr-kpi-card cyan">
                <div className="qr-kpi-label">En Cours d'Intervention</div>
                <div className="qr-kpi-value text-info">{stats?.kpis?.inProgress || 0}</div>
                <div className="qr-kpi-sub">Prises en charge par les techniciens</div>
              </div>
            </div>

            <div className="col-md-3">
              <div className="qr-kpi-card green">
                <div className="qr-kpi-label">Taux de Résolution</div>
                <div className="qr-kpi-value text-success">{stats?.kpis?.resolutionRate || 0}%</div>
                <div className="qr-kpi-sub">
                  <strong>{stats?.kpis?.resolved || 0}</strong> signalements résolus
                </div>
              </div>
            </div>
          </div>

          {/* Graphiques analytiques visuels ultra-lisibles (Progress Bars CSS/SVG sans dépendance Chart.js) */}
          <div className="row g-4">
            {/* Répartition par Motif */}
            <div className="col-md-6">
              <div className="qr-card h-100 mb-0">
                <div className="qr-card-header">
                  <span>📊 Répartition par Motif de Panne</span>
                </div>
                <div className="qr-card-body">
                  {!stats?.byCategory || stats.byCategory.length === 0 ? (
                    <div className="text-center py-5 text-muted">Aucun signalement enregistré.</div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {stats.byCategory.map((cat, idx) => {
                        const total = stats.kpis?.total || 1;
                        const pct = Math.round((cat.count / total) * 100);
                        const colors = ['#ef4444', '#f59e0b', '#2563eb', '#10b981', '#8b5cf6', '#ec4899'];
                        const color = colors[idx % colors.length];
                        return (
                          <div key={idx}>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="fw-bold text-dark">{cat._id}</span>
                              <span className="fw-bold text-dark">{cat.count} ({pct}%)</span>
                            </div>
                            <div className="progress" style={{ height: '10px', borderRadius: '6px', background: '#e2e8f0' }}>
                              <div
                                className="progress-bar"
                                style={{ width: `${pct}%`, backgroundColor: color, borderRadius: '6px' }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Top Équipements touchés */}
            <div className="col-md-6">
              <div className="qr-card h-100 mb-0">
                <div className="qr-card-header">
                  <span>🛋️ Top des Équipements les plus fréquemment en Panne</span>
                </div>
                <div className="qr-card-body">
                  {!stats?.byEquipment || stats.byEquipment.length === 0 ? (
                    <div className="text-center py-5 text-muted">Aucun équipement en panne.</div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {stats.byEquipment.map((eq, idx) => {
                        const maxCount = Math.max(...stats.byEquipment.map(e => e.count), 1);
                        const pct = Math.round((eq.count / maxCount) * 100);
                        return (
                          <div key={idx}>
                            <div className="d-flex justify-content-between mb-1">
                              <span className="fw-bold text-dark">🛋️ {eq.name} <small className="text-muted">({eq.locationName})</small></span>
                              <span className="badge bg-danger fs-6">{eq.count} pannes</span>
                            </div>
                            <div className="progress" style={{ height: '10px', borderRadius: '6px', background: '#e2e8f0' }}>
                              <div
                                className="progress-bar bg-danger"
                                style={{ width: `${pct}%`, borderRadius: '6px' }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Signalements par Localisation */}
            <div className="col-md-12">
              <div className="qr-card mb-0">
                <div className="qr-card-header">
                  <span>📍 Signalements par Localisation / Site</span>
                </div>
                <div className="qr-card-body">
                  {!stats?.byLocation || stats.byLocation.length === 0 ? (
                    <div className="text-center py-4 text-muted">Aucune donnée par site.</div>
                  ) : (
                    <div className="row g-3">
                      {stats.byLocation.map((loc, idx) => (
                        <div className="col-md-4" key={idx}>
                          <div className="p-3 border rounded-3 bg-light text-center">
                            <div className="fs-5 fw-bold text-primary">📍 {loc.name}</div>
                            <div className="fs-2 fw-bold text-dark mt-1">{loc.count}</div>
                            <small className="text-muted">signalements enregistrés</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL POSTER QR CODE IMPRIMABLE & PARTAGE EXTERNE ================= */}
      {posterEquip && (
        <div className="modal fade show d-block qr-modal-backdrop" tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header bg-dark text-white">
                <h5 className="modal-title fw-bold">🖨️ Affiche QR & Lien Externe Client</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setPosterEquip(null)}></button>
              </div>

              <div className="modal-body text-center p-4">
                <div className="qr-poster-box" id="printable-qr-poster">
                  <div className="qr-poster-header">
                    <span className="qr-poster-badge">✨ SERVICE CLIENT & ASSISTANCE</span>
                    <h3>📍 {posterEquip.location?.name || 'NOTRE ESPACE'}</h3>
                    <h4>🛋️ {posterEquip.name}</h4>
                  </div>
                  
                  <div className="qr-poster-qr-frame my-3">
                    <img src={posterQrUrl} alt="QR Code Client Externe" className="qr-poster-img" />
                    <div className="qr-scan-badge">📱 Scannez-moi</div>
                  </div>

                  <div className="qr-poster-instructions">
                    <p className="fw-bold fs-5 text-primary mb-1">Une panne ou une suggestion ?</p>
                    <p className="text-muted small">
                      Scannez ce QR Code avec l'appareil photo de votre smartphone pour accéder directement au formulaire de signalement avec photo.
                    </p>
                  </div>

                  <div className="qr-poster-footer mt-3">
                    <small className="text-muted font-monospace d-block text-break">
                      🌐 {getClientFormUrl(posterEquip.qrToken)}
                    </small>
                  </div>
                </div>

                <div className="qr-external-share-box mt-4 p-3 bg-light rounded-3 border">
                  <h6 className="fw-bold text-dark mb-2">🔗 Partager ce Lien Externe aux Clients :</h6>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <button
                      className="btn btn-outline-success btn-sm fw-bold"
                      onClick={() => {
                        const url = getClientFormUrl(posterEquip.qrToken);
                        const waText = encodeURIComponent(`Bonjour, voici le lien direct pour effectuer vos réclamations & suggestions pour ${posterEquip.name} (${posterEquip.location?.name}) :\n${url}`);
                        window.open(`https://wa.me/?text=${waText}`, '_blank');
                      }}
                    >
                      💬 Partager sur WhatsApp
                    </button>

                    <button
                      className="btn btn-outline-primary btn-sm fw-bold"
                      onClick={() => {
                        window.open(getClientFormUrl(posterEquip.qrToken), '_blank');
                      }}
                    >
                      🌐 Ouvrir la Vue Client (Nouvel Onglet)
                    </button>

                    <button
                      className="btn btn-outline-secondary btn-sm fw-bold"
                      onClick={() => handleCopyUrl(posterEquip.qrToken)}
                    >
                      📋 Copier le Lien Externe
                    </button>
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary fw-bold" onClick={() => setPosterEquip(null)}>Fermer</button>
                <button type="button" className="btn btn-primary fw-bold" onClick={handlePrintPoster}>🖨️ Imprimer l'Affiche QR</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= MODAL AGGRANDISSEMENT PHOTO CLIENT ================= */}
      {selectedPhoto && (
        <div className="modal fade show d-block qr-modal-backdrop" tabIndex="-1" onClick={() => setSelectedPhoto(null)}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content bg-transparent border-0 text-center">
              <img src={selectedPhoto} alt="Photo signalement grand format" className="img-fluid rounded-4 shadow-lg border border-3 border-white" style={{ maxHeight: '85vh', objectFit: 'contain' }} />
              <div className="mt-3 text-white fw-bold bg-dark bg-opacity-75 d-inline-block mx-auto px-4 py-2 rounded-pill">
                🔍 Cliquer n'importe où pour fermer
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRManagement;
